/**
 * PLM QC evaluation — pairs each QC re-read with the layer it re-reads and
 * judges the pair (prototype v8, "QC Result View").
 *
 * Pure functions over the rows `AI_Plm_QcCompare_SP` returns: no React, no
 * fetching, so the rules that decide a lab's pass/fail are unit-testable on
 * their own.
 *
 * THE FOUR CHECKS a duplicate must satisfy, all from EPA 600/R-93/116:
 *   1. Same CLASSIFICATION — both positive (> 1% asbestos) or both negative.
 *   2. Same asbestos TYPES — Chrysotile vs Amosite is a failure even at the
 *      same percentage.
 *   3. The re-read's quantity inside the EPA 600 ACCEPTABLE RANGE for the
 *      original's reading.
 *   4. The right ANALYST — an Intra re-read is the same analyst re-reading
 *      their own slide; an Inter re-read must be somebody else.
 *
 * NOT MODELLED YET (no table on this database): reference/calibration slides
 * and the lab's own ±2σ/±3σ control limits. The prototype shows both; they
 * need a Calibration Key and a control-chart history first, so nothing here
 * invents them.
 */
import type { PlmQcCompareRow } from './plm.api';

/** Above this percentage a layer is classified positive (asbestos-containing). */
export const POSITIVE_THRESHOLD_PCT = 1;

/** The FiberTypes value that marks a component as asbestos. */
export const ASBESTOS_COMPONENT = 'Asbestos Fibers';

/** The QC types that are re-reads of another layer. */
const RE_READ = new Set(['Intra Re-read', 'Inter Re-read']);

/**
 * EPA 600 "Acceptable Mean Results" anchors: [reported %, acceptable low,
 * acceptable high]. The lab's sheet interpolates linearly between anchors
 * (its FORECAST.LINEAR columns), which {@link epaRange} reproduces.
 */
const EPA600: ReadonlyArray<readonly [number, number, number]> = [
  [0.5, 0.5, 2], [2, 0.5, 5], [5, 2, 9], [10, 5, 15], [20, 10, 30],
  [30, 20, 40], [40, 30, 50], [50, 40, 60], [60, 50, 70], [70, 60, 80],
  [80, 65, 95], [90, 70, 100], [100, 80, 100],
];

/** The acceptable low/high band for a reported percentage. */
export function epaRange(pct: number): { lo: number; hi: number } {
  const first = EPA600[0]!;
  if (pct <= first[0]) return { lo: first[1], hi: first[2] };
  for (let k = 1; k < EPA600.length; k++) {
    const [a0, l0, h0] = EPA600[k - 1]!;
    const [a1, l1, h1] = EPA600[k]!;
    if (pct <= a1) {
      const t = (pct - a0) / (a1 - a0);
      return {
        lo: Number((l0 + t * (l1 - l0)).toFixed(2)),
        hi: Number((h0 + t * (h1 - h0)).toFixed(2)),
      };
    }
  }
  return { lo: 80, hi: 100 };
}

/** A parsed result: the number, and whether it was written as "< x". */
export type ParsedResult = { lessThan: boolean; value: number; raw: string };

/**
 * Reads a legacy free-text result. "12.54" · "<0.25%" · " 5 " all parse;
 * "Trace", "ND" and blank return null — they are readings, but not quantities,
 * so they must never be compared as numbers.
 */
export function parseResult(text: string | null | undefined): ParsedResult | null {
  const raw = String(text ?? '').trim();
  if (!raw) return null;
  const lessThan = raw.startsWith('<');
  const value = Number.parseFloat(raw.replace(/[<%\s]/g, ''));
  return Number.isNaN(value) ? null : { lessThan, value, raw };
}

/** The lab's quantitative reading: "<x" counts as HALF of x (their sheets use
 *  "<1" → 0.5), a plain number as itself. */
export const quantity = (p: ParsedResult | null): number | null =>
  !p ? null : (p.lessThan ? p.value / 2 : p.value);

/** The CLASSIFYING reading: below the reporting limit counts as 0, so "<1"
 *  never classifies a layer positive. */
export const classifyValue = (p: ParsedResult | null): number =>
  !p ? 0 : (p.lessThan ? 0 : p.value);

/** One layer, with the components it reported. */
export interface QcLayer {
  sequenceOid: string;
  sampleLogInOid: string | null;
  sampleLayer: string | null;
  layerLetter: string | null;
  qcType: string | null;
  status: number;
  analyzedByOid: string | null;
  analyzedByName: string | null;
  noAsbestosDetected: boolean;
  components: { examOid: string | null; component: string | null; name: string | null; result: string | null }[];
  /** Withheld from this user (the SIMPLE dup flow, 2026-10-17): identity only. */
  blind?: boolean;
}

/** Folds the flat SP rows into one entry per layer, order preserved. */
export function groupLayers(rows: readonly PlmQcCompareRow[]): QcLayer[] {
  const out: QcLayer[] = [];
  const byOid = new Map<string, QcLayer>();
  for (const r of rows) {
    let layer = byOid.get(r.sequenceOid);
    if (!layer) {
      layer = {
        sequenceOid: r.sequenceOid,
        sampleLogInOid: r.sampleLogInOid,
        sampleLayer: r.sampleLayer,
        layerLetter: r.layerLetter,
        qcType: r.qcType,
        status: r.status,
        analyzedByOid: r.analyzedByOid,
        analyzedByName: r.analyzedByName,
        noAsbestosDetected: r.noAsbestosDetected,
        components: [],
        blind: !!r.blind,
      };
      byOid.set(r.sequenceOid, layer);
      out.push(layer);
    }
    // A layer with no exam rows arrives as ONE row with null component columns.
    if (r.examOid) {
      layer.components.push({
        examOid: r.examOid, component: r.component, name: r.name, result: r.result,
      });
    }
  }
  return out;
}

const asbestosOf = (l: QcLayer) =>
  l.components.filter(c => c.component === ASBESTOS_COMPONENT);

/** Positive when any asbestos component reads over the threshold. */
export function classification(l: QcLayer): 'Positive' | 'Negative' {
  return asbestosOf(l).some(c => classifyValue(parseResult(c.result)) > POSITIVE_THRESHOLD_PCT)
    ? 'Positive' : 'Negative';
}

/** The asbestos types found, sorted, so two layers compare regardless of the
 *  order they were typed in. "None" when there are none. */
export function asbestosTypes(l: QcLayer): string {
  const names = asbestosOf(l).map(c => c.name ?? '').filter(Boolean).sort();
  return names.length ? names.join(', ') : 'None';
}

/** One-line read-back of a layer's asbestos, for the QC table's two columns. */
export function asbestosSummary(l: QcLayer): string {
  const a = asbestosOf(l);
  if (!a.length) return l.noAsbestosDetected ? 'No Asbestos Detected' : 'None Detected';
  return a.map(c => `${c.name ?? '—'} ${c.result ?? '—'}`).join(', ');
}

/** One check in the pair's verdict. `na` = not applicable, which passes. */
export interface QcCheck { label: string; ok: boolean; na?: boolean }

/** One component compared across the two layers. */
export interface QcComponentRow {
  name: string;
  component: string | null;
  isAsbestos: boolean;
  original: ParsedResult | null;
  reRead: ParsedResult | null;
  /** re-read minus original, in the lab's quantitative terms. */
  error: number | null;
  range: { lo: number; hi: number } | null;
  state: 'ok' | 'bad' | 'info';
}

export interface QcPair {
  /** The re-read layer — the row the tab lists. */
  reRead: QcLayer;
  /** The layer it re-reads, or null when it cannot be found. */
  original: QcLayer | null;
  /** 'Intra' | 'Inter', from the re-read's QC type. */
  kind: string;
  state: 'pass' | 'fail' | 'pending';
  /** Set when the pair could not be judged at all. */
  reason?: string;
  rows: QcComponentRow[];
  checks: QcCheck[];
}

/**
 * Builds the QC pairs for a batch.
 *
 * A re-read finds its original by the FOREIGN KEY it shares with it
 * (`sampleLogInOid`) plus the layer letter — never by parsing the display
 * string, which would mis-split on a client sample id containing
 * " Intra Re-read".
 */
export function buildQcPairs(layers: readonly QcLayer[]): QcPair[] {
  const originals = layers.filter(l => !RE_READ.has(l.qcType ?? ''));
  return layers
    .filter(l => RE_READ.has(l.qcType ?? ''))
    .map(reRead => {
      const original = originals.find(o =>
        o.sampleLogInOid != null
        && o.sampleLogInOid === reRead.sampleLogInOid
        && (o.layerLetter ?? '') === (reRead.layerLetter ?? '')) ?? null;
      return evaluatePair(reRead, original);
    });
}

/** Judges one re-read against its original. Exported for the unit tests. */
export function evaluatePair(reRead: QcLayer, original: QcLayer | null): QcPair {
  const kind = (reRead.qcType ?? '').startsWith('Inter') ? 'Inter' : 'Intra';

  if (!original) {
    return {
      reRead, original, kind, state: 'fail', rows: [], checks: [],
      reason: `The layer this re-reads was not found in the batch.`,
    };
  }
  // Nothing entered yet: not a failure, just not done.
  if (reRead.status < 2 || reRead.components.length === 0) {
    return { reRead, original, kind, state: 'pending', rows: [], checks: [] };
  }

  const names = [...new Set([
    ...original.components.map(c => c.name ?? ''),
    ...reRead.components.map(c => c.name ?? ''),
  ])].filter(Boolean);

  const rows: QcComponentRow[] = names.map(name => {
    const co = original.components.find(c => c.name === name);
    const cd = reRead.components.find(c => c.name === name);
    const component = (co ?? cd)?.component ?? null;
    const isAsbestos = component === ASBESTOS_COMPONENT;
    const a = parseResult(co?.result);
    const b = parseResult(cd?.result);

    let error: number | null = null;
    if (a && b) error = Number(((quantity(b) ?? 0) - (quantity(a) ?? 0)).toFixed(2));

    let range: { lo: number; hi: number } | null = null;
    let state: QcComponentRow['state'] = 'info';
    if (isAsbestos) {
      range = a ? epaRange(quantity(a) ?? 0) : null;
      const bothBelowLimit = !!a?.lessThan && !!b?.lessThan;
      const inRange = !!a && !!b && !!range
        && (quantity(b) ?? 0) >= range.lo && (quantity(b) ?? 0) <= range.hi;
      // A component only one side reported is an automatic miss.
      state = (!co || !cd) ? 'bad' : (bothBelowLimit || inRange) ? 'ok' : 'bad';
    }
    return { name, component, isAsbestos, original: a, reRead: b, error, range, state };
  });

  const classO = classification(original);
  const classD = classification(reRead);
  const typesO = asbestosTypes(original);
  const typesD = asbestosTypes(reRead);
  const asbRows = rows.filter(r => r.isAsbestos);
  const sameAnalyst = !!original.analyzedByOid && !!reRead.analyzedByOid
    && original.analyzedByOid.toLowerCase() === reRead.analyzedByOid.toLowerCase();

  const checks: QcCheck[] = [
    {
      label: `Classification ${classO} / ${classD}`,
      ok: classO === classD,
    },
    {
      label: `Asbestos type ${typesO === typesD ? typesO : `${typesO} ≠ ${typesD}`}`,
      ok: typesO === typesD,
    },
    {
      label: asbRows.length
        ? `Re-read inside EPA 600 range (${asbRows
            .map(r => (r.range ? `${r.range.lo}–${r.range.hi}%` : '—')).join(', ')})`
        : 'Quantity — no asbestos',
      ok: asbRows.every(r => r.state === 'ok'),
      na: asbRows.length === 0,
    },
    {
      label: kind === 'Intra'
        ? `Same analyst (${original.analyzedByName || '—'} / ${reRead.analyzedByName || '—'})`
        : `Different analyst (${original.analyzedByName || '—'} / ${reRead.analyzedByName || '—'})`,
      ok: kind === 'Intra' ? sameAnalyst : !sameAnalyst,
    },
  ];

  return {
    reRead, original, kind,
    state: checks.every(c => c.ok || c.na) ? 'pass' : 'fail',
    rows, checks,
  };
}
